import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useGraphEditorStore } from '@/graph-store';
import { useOperationCatalog } from '@/api/useOperationCatalog';
import { useToast } from '@/hooks/useToast';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';
import { TranslationGenerator } from './TranslationGenerator';
import { StepTranslationRow } from './StepTranslationRow';
import type { OperationNodeData, GraphSnapshotLight, GraphMaterial, StepIconType } from './translation.types';
import type { OperationNodeSpec, OperationTypeDefinition } from '@/graph/domain/types';
import { generateMaterialLabel } from '@/graph/domain/labelGen';

interface TranslationPanelProps {
  editable: boolean;
  highlightedStepIds: Set<string>;
  activeIcon: { nodeId: string; iconType: StepIconType } | null;
  onStepClicked: (nodeId: string) => void;
  onStepEditRequested?: (nodeId: string) => void;
  onParameterChanged?: (nodeId: string, paramName: string, value: unknown) => void;
  onCheckpointChanged?: (nodeId: string, cpId: string, value: unknown) => void;
  onIconClicked: (nodeId: string, iconType: StepIconType) => void;
}

/**
 * Build the lightweight graph snapshot that TranslationGenerator needs.
 */
function buildGraphSnapshot(state: ReturnType<typeof useGraphEditorStore.getState>): GraphSnapshotLight {
  const { materialNodes, operationNodes, flowEdges, groups } = state.graphData;

  const materials = new Map<string, GraphMaterial>();
  for (const [id, mat] of Object.entries(materialNodes)) {
    materials.set(id, {
      id,
      name: mat.name,
      containerName: mat.container.name,
      constituentExpr: generateMaterialLabel(mat.constituents),
      constituents: mat.constituents ? mat.constituents.map((c) => ({ ...c })) : [],
    });
  }

  // Build adjacency from edges: material→operation = input, operation→material = output
  const inputMaterials = new Map<string, string[]>();
  const outputMaterials = new Map<string, string[]>();

  for (const edge of Object.values(flowEdges)) {
    const source = edge.sourceId;
    const target = edge.targetId;

    // Source is material, target is operation → input
    if (materialNodes[source] && operationNodes[target]) {
      const list = inputMaterials.get(target) ?? [];
      list.push(source);
      inputMaterials.set(target, list);
    }

    // Source is operation, target is material → output
    if (operationNodes[source] && materialNodes[target]) {
      const list = outputMaterials.get(source) ?? [];
      list.push(target);
      outputMaterials.set(source, list);
    }
  }

  const groupsMap = new Map<string, { label: string; stepIds: string[]; cycleRanges?: Array<{ id: string; startIndex: number; endIndex: number; iterationCount: number }> }>();
  for (const [id, group] of Object.entries(groups)) {
    groupsMap.set(id, {
      label: group.label,
      stepIds: group.stepIds,
      cycleRanges: group.cycleRanges?.map((cr) => ({
        id: cr.id,
        startIndex: cr.startIndex,
        endIndex: cr.endIndex,
        iterationCount: cr.iterationCount,
      })),
    });
  }

  return { materials, inputMaterials, outputMaterials, groups: groupsMap };
}

function toOperationNodeData(id: string, spec: OperationNodeSpec): OperationNodeData {
  return {
    id,
    archetype: spec.archetype,
    operationTypeId: spec.operationTypeId,
    displayName: spec.displayName,
    parameters: spec.parameters,
    checkPoints: spec.checkPoints,
    duration: spec.duration,
    creationOrder: spec.creationOrder ?? 0,
    groupId: spec.groupId,
    orderInGroup: spec.orderInGroup,
    notes: spec.notes,
    comments: spec.comments,
    approvers: spec.approvers,
    notifications: spec.notifications,
    yield: spec.yield,
    transferRow: spec.transferRow as OperationNodeData['transferRow'],
  };
}

export const TranslationPanel: React.FC<TranslationPanelProps> = (props) => {
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const groups = useGraphEditorStore((s) => s.graphData.groups);
  const openModal = useGraphEditorStore((s) => s.openModal);
  const isProtocolLocked = useGraphEditorStore((s) => s.ui.isProtocolLocked ?? false);
  const isLifecycleLocked = useGraphEditorStore((s) => s.ui.isLifecycleLocked ?? false);
  const isArchived = useGraphEditorStore((s) => s.ui.isArchived ?? false);
  const isArchivalInProgress = useGraphEditorStore((s) => s.ui.isArchivalInProgress ?? false);
  const runsExist = useGraphEditorStore((s) => s.ui.runsExist ?? false);
  const canEditContent = useGraphEditorStore((s) => s.ui.canEditContent ?? true);
  const { data: catalogList } = useOperationCatalog();
  const { showWarning } = useToast();

  // Multi-select state for grouping
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedStepIds, setSelectedStepIds] = useState<Set<string>>(new Set());

  const catalog = useMemo(() => {
    const map = new Map<string, OperationTypeDefinition>();
    for (const item of catalogList) {
      map.set(item.id, item);
    }
    return map;
  }, [catalogList]);

  const translations = useMemo(() => {
    const state = useGraphEditorStore.getState();
    const graph = buildGraphSnapshot(state);

    const sorted = Object.entries(operationNodes)
      .map(([id, spec]) => toOperationNodeData(id, spec))
      .sort((a, b) => a.creationOrder - b.creationOrder);

    return TranslationGenerator.generateAll(sorted, graph, props.editable, catalog);
  }, [operationNodes, materialNodes, flowEdges, groups, props.editable, catalog]);

  const handleSelectionToggle = useCallback((nodeId: string) => {
    setSelectedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const handleGroupSelected = useCallback(() => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || canEditContent === false) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    
    if (selectedStepIds.size === 0) return;
    // Ensure contiguous selection (sort by creationOrder)
    const sortedOps = Object.values(operationNodes)
      .filter((op) => op.id && selectedStepIds.has(op.id))
      .sort((a, b) => (a.creationOrder ?? 0) - (b.creationOrder ?? 0));
    const stepIds = sortedOps.map((op) => op.id!);

    openModal({
      kind: 'add-group',
      payload: { stepIds },
    });
    setSelectionMode(false);
    setSelectedStepIds(new Set());
  }, [selectedStepIds, operationNodes, openModal, isProtocolLocked, isLifecycleLocked, isArchived, isArchivalInProgress, runsExist, canEditContent, showWarning]);

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedStepIds(new Set());
  }, []);

  // Create a version key that changes when materialNodes composition changes
  // This forces StepTranslationRow (memoized) to unmount/remount with new data
  const materialNodesVersion = useMemo(() => {
    const matIds = Object.keys(materialNodes).sort();
    const compositions = matIds.map((id) => {
      const mat = materialNodes[id];
      return `${id}:${mat.composition?.constituents?.length ?? 0}:${mat.container?.name}`;
    }).join('|');
    return compositions;
  }, [materialNodes]);

  // Auto-scroll to highlighted step
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.highlightedStepIds.size > 0 && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [props.highlightedStepIds]);

  if (translations.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          No steps in the protocol yet. Add operations to the canvas to see the translation.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #e5e7eb',
          bgcolor: selectionMode ? '#f0f9ff' : 'transparent',
          flexShrink: 0,
        }}
      >
        {!selectionMode ? (
          <Button
            size="small"
            variant="text"
            onClick={() => setSelectionMode(true)}
            disabled={translations.length < 2 || isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist}
          >
            Group Steps
          </Button>
        ) : (
          <>
            <Typography variant="caption" sx={{ flex: 1 }}>
              {selectedStepIds.size} step{selectedStepIds.size !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              disabled={selectedStepIds.size < 2}
              onClick={handleGroupSelected}
            >
              Group
            </Button>
            <Button size="small" variant="text" onClick={handleCancelSelection}>
              Cancel
            </Button>
          </>
        )}
      </Box>

      {/* Steps list */}
      <Box ref={containerRef} sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {translations.map((t, index) => {
          return (
            <Box
              key={`${t.nodeId}-${materialNodesVersion}`}
              ref={props.highlightedStepIds.has(t.nodeId) ? highlightedRef : undefined}
            >
              <StepTranslationRow
                translation={t}
                highlighted={props.highlightedStepIds.has(t.nodeId)}
                highlightedStepIds={props.highlightedStepIds}
                editable={props.editable}
                activeIconType={props.activeIcon?.nodeId === t.nodeId ? props.activeIcon.iconType : null}
                selectable={selectionMode}
                selected={selectedStepIds.has(t.nodeId)}
                onSelectionToggle={handleSelectionToggle}
                onStepClicked={props.onStepClicked}
                onParameterChanged={props.onParameterChanged}
                onCheckpointChanged={props.onCheckpointChanged}
                onIconClicked={props.onIconClicked}
                onEditClicked={props.onStepEditRequested}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
